import { motion, useReducedMotion } from "framer-motion";

/**
 * Section
 * Reusable section wrapper with a consistent inner column,
 * subtle entrance animation (respects prefers-reduced-motion),
 * an optional eyebrow + title header, and a slot for content.
 */
export function Section({
  id,
  eyebrow,
  title,
  subtitle,
  align = "left",
  className = "",
  children,
  bare = false,
  testId,
}) {
  const reduce = useReducedMotion();
  const initial = reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 };
  const whileInView = { opacity: 1, y: 0 };

  return (
    <section
      id={id}
      data-testid={testId}
      className={`relative w-full border-t border-border/60 ${className}`}
    >
      <div className="mx-auto w-full max-w-6xl px-6 py-20 sm:py-28">
        {!bare && (eyebrow || title || subtitle) && (
          <motion.header
            initial={initial}
            whileInView={whileInView}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={`mb-12 ${align === "center" ? "text-center mx-auto max-w-2xl" : "max-w-2xl"}`}
          >
            {eyebrow && (
              <div
                className={`mb-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground ${
                  align === "center" ? "justify-center" : ""
                }`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
                {eyebrow}
              </div>
            )}
            {title && (
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-5 font-body text-base text-muted-foreground sm:text-lg">
                {subtitle}
              </p>
            )}
          </motion.header>
        )}
        <motion.div
          initial={initial}
          whileInView={whileInView}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.08 }}
        >
          {children}
        </motion.div>
      </div>
    </section>
  );
}
